import React, { useState, useEffect } from "react"
import { fetchAllOptions, updateOption } from "../../HTTP/admin"
import SideBarAdmin from "./SideBarAdmin"
import ReactQuill from "react-quill"

export default function OptionsAdmin() {

    const localUser = JSON.parse(localStorage.getItem("user"))
    if (!localUser.super_admin) {
        window.location.href = '/'
    }
    
    const [options, setOptions] = useState([]);
    const [formData, setFormData] = useState({});
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchOptions = async () => {
            try {
                setIsLoading(true);
                const resFetchOptions = await fetchAllOptions();
                setOptions(resFetchOptions);
                
                // Initialize formData with default values
                const initialFormData = resFetchOptions.reduce((acc, option) => ({
                    ...acc,
                    [option.key]: option.value || '' // Ensure empty string as fallback
                }), {});
                
                setFormData(initialFormData);
            } catch (error) {
                console.error("Error fetching options:", error);
            } finally {
                setIsLoading(false);
            }
        };
        
        fetchOptions();
    }, []);

    const handleChange = (e) => {
        const { name, value, type, checked, files } = e.target;
        
        // Handle different input types
        if (type === 'checkbox') {
            setFormData(prevData => ({
                ...prevData,
                [name]: checked ? '1' : '0'
            }));
        } else if (type === 'file') {
            // For file input, we'll store the file object
            setFormData(prevData => ({
                ...prevData,
                [name]: files[0]
            }));
        } else if (type === 'number') {
            setFormData(prevData => ({
                ...prevData,
                [name]: value === '' ? '' : Number(value)
            }));
        } else {
            setFormData(prevData => ({
                ...prevData,
                [name]: value
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const res = await updateOption(formData);
            console.log(res);
            window.location.reload();
        } catch (error) {
            console.error("Error updating options:", error);
        }
    };

    // Function to render the appropriate input based on type
    const renderInput = (option) => {
        const { key, type, options: selectOptions } = option;
        
        switch (type) {
            case 'textarea':
                return (
                    <textarea
                        id={key}
                        name={key}
                        value={formData[key] || ''}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        rows="4"
                    />
                );
            case 'checkbox':
                return (
                    <input
                        type="checkbox"
                        id={key}
                        name={key}
                        checked={formData[key] === '1'}
                        onChange={handleChange}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                );
            case 'select':
                return (
                    <select
                        id={key}
                        name={key}
                        value={formData[key] || ''}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                        <option value="">Sélectionnez une option</option>
                        {selectOptions && selectOptions.map((opt, idx) => (
                            <option key={idx} value={opt.value || opt}>
                                {opt.label || opt}
                            </option>
                        ))}
                    </select>
                );
            case 'number':
                return (
                    <input
                        type="number"
                        id={key}
                        name={key}
                        value={formData[key] || ''}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                );
            case 'color':
                return (
                    <input
                        type="color"
                        id={key}
                        name={key}
                        value={formData[key] || '#000000'}
                        onChange={handleChange}
                        className="h-10 w-full"
                    />
                );
            case 'date':
                return (
                    <input
                        type="date"
                        id={key}
                        name={key}
                        value={formData[key] || ''}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                );
            case 'file':
                return (
                    <input
                        type="file"
                        id={key}
                        name={key}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                );
            case 'radio':
                return (
                    <div className="flex flex-col space-y-2">
                        {selectOptions && selectOptions.map((opt, idx) => (
                            <div key={idx} className="flex items-center">
                                <input
                                    type="radio"
                                    id={`${key}-${idx}`}
                                    name={key}
                                    value={opt.value || opt}
                                    checked={formData[key] === (opt.value || opt)}
                                    onChange={handleChange}
                                    className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                />
                                <label htmlFor={`${key}-${idx}`} className="ml-2 block text-sm text-gray-700">
                                    {opt.label || opt}
                                </label>
                            </div>
                        ))}
                    </div>
                );
            case 'password':
                return (
                    <input
                        type="password"
                        id={key}
                        name={key}
                        value={formData[key] || ''}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                );
            case 'switch':
                return (
                    <div className="relative inline-block w-10 mr-2 align-middle select-none">
                        <input
                            type="checkbox"
                            id={key}
                            name={key}
                            checked={!!formData[key]}
                            onChange={handleChange}
                            className="absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                        />
                        <label
                            htmlFor={key}
                            className={`block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer ${
                                formData[key] ? 'bg-blue-500' : ''
                            }`}
                        ></label>
                    </div>
                );
            case 'wysiwyg':
                return (
                    <div className="mb-16">
                        <ReactQuill
                            id={key}
                            name={key}
                            value={formData[key] || ''}
                            onChange={(value) => setFormData(prevData => ({ ...prevData, [key]: value }))}
                            placeholder="Écrivez votre contenu ici..."
                            className="mb-5 notranslate"
                            style={{ height: '200px' }}
                            theme="snow"
                        />
                    </div>
                );
            default:
                return (
                    <input
                        type="text"
                        id={key}
                        name={key}
                        value={formData[key] || ''}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                );
        }
    };

    if (isLoading) {
        return (
            <div className="flex">
                <SideBarAdmin />
                <div className="max-w-md mx-auto mt-8 p-6 bg-white rounded-lg shadow-md">
                    <p className="text-center">Loading...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex mt-12">
            <div className="max-w-md mx-auto p-6 bg-white rounded-lg shadow-md md:overflow-y-auto" style={{ maxHeight: window.innerWidth >= 768 ? 'calc(100vh - 100px)' : undefined }}>
                <h2 className="text-2xl font-bold mb-6 text-center">Options</h2>
                <form onSubmit={handleSubmit}>
                    {options.map((option, index) => (
                        <div className="mb-4" key={option.key || index}>
                            <label htmlFor={option.key} className="block text-gray-700 text-sm font-bold mb-2">
                                {option.label}
                            </label>
                            {renderInput(option)}
                            {option.description && (
                                <p className="mt-1 text-xs text-gray-500">{option.description}</p>
                            )}
                        </div>
                    ))}
                    <div className="flex items-center justify-end">
                        <button
                            type="submit"
                            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                        >
                            Enregistrer
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}